/** CSS border styles that may be resolved from the themed Page surface. */
export const WIDGET_FRAME_RESOLVED_BORDER_STYLES = [
    "none",
    "hidden",
    "dotted",
    "dashed",
    "solid",
    "double",
    "groove",
    "ridge",
    "inset",
    "outset",
];
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const hasOnlyKeys = (value, keys) => {
    const valueKeys = Object.keys(value);
    return (valueKeys.length === keys.length &&
        keys.every((key) => Object.hasOwn(value, key)));
};
const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
const isRgbChannel = (value) => isFiniteNumber(value) &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 255;
const isAlpha = (value) => isFiniteNumber(value) && value >= 0 && value <= 1;
const isWidgetFrameResolvedBorderStyle = (value) => typeof value === "string" &&
    WIDGET_FRAME_RESOLVED_BORDER_STYLES.some((style) => style === value);
const isWidgetFrameResolvedSrgbColor = (value) => {
    if (!isRecord(value) ||
        !hasOnlyKeys(value, ["colorSpace", "red", "green", "blue", "alpha"])) {
        return false;
    }
    return (value.colorSpace === "srgb" &&
        isRgbChannel(value.red) &&
        isRgbChannel(value.green) &&
        isRgbChannel(value.blue) &&
        isAlpha(value.alpha));
};
const isWidgetFrameResolvedBorder = (value) => {
    if (!isRecord(value) || !hasOnlyKeys(value, ["color", "style", "widthPx"])) {
        return false;
    }
    return (isWidgetFrameResolvedSrgbColor(value.color) &&
        isWidgetFrameResolvedBorderStyle(value.style) &&
        isFiniteNumber(value.widthPx) &&
        value.widthPx >= 0);
};
const isWidgetFrameResolvedBorderRadiusCorner = (value) => isRecord(value) &&
    hasOnlyKeys(value, ["horizontalPx", "verticalPx"]) &&
    isFiniteNumber(value.horizontalPx) &&
    value.horizontalPx >= 0 &&
    isFiniteNumber(value.verticalPx) &&
    value.verticalPx >= 0;
const isWidgetFrameResolvedBorderRadius = (value) => isRecord(value) &&
    hasOnlyKeys(value, ["topLeft", "topRight", "bottomRight", "bottomLeft"]) &&
    isWidgetFrameResolvedBorderRadiusCorner(value.topLeft) &&
    isWidgetFrameResolvedBorderRadiusCorner(value.topRight) &&
    isWidgetFrameResolvedBorderRadiusCorner(value.bottomRight) &&
    isWidgetFrameResolvedBorderRadiusCorner(value.bottomLeft);
const isWidgetFrameResolvedBoxShadowLayer = (value) => isRecord(value) &&
    hasOnlyKeys(value, [
        "color",
        "offsetXPx",
        "offsetYPx",
        "blurRadiusPx",
        "spreadRadiusPx",
        "inset",
    ]) &&
    isWidgetFrameResolvedSrgbColor(value.color) &&
    isFiniteNumber(value.offsetXPx) &&
    isFiniteNumber(value.offsetYPx) &&
    isFiniteNumber(value.blurRadiusPx) &&
    value.blurRadiusPx >= 0 &&
    isFiniteNumber(value.spreadRadiusPx) &&
    typeof value.inset === "boolean";
const isWidgetFrameResolvedBoxShadow = (value) => {
    if (!Array.isArray(value) || Object.keys(value).length !== value.length) {
        return false;
    }
    for (let index = 0; index < value.length; index += 1) {
        if (!Object.hasOwn(value, index) ||
            !isWidgetFrameResolvedBoxShadowLayer(value[index])) {
            return false;
        }
    }
    return true;
};
export function isWidgetFrameAppearance(value) {
    if (!isRecord(value)) {
        return false;
    }
    if (value.kind === "none") {
        return hasOnlyKeys(value, ["kind"]);
    }
    const requiredPageKeys = [
        "kind",
        "border",
        "borderRadius",
        "boxShadow",
    ];
    const optionalPageKeys = [
        "resolvedBorder",
        "resolvedBorderRadius",
        "resolvedBoxShadow",
    ];
    const allowedPageKeys = [...requiredPageKeys, ...optionalPageKeys];
    const pageKeys = Object.keys(value);
    const hasRequiredPageKeys = requiredPageKeys.every((key) => pageKeys.some((pageKey) => pageKey === key));
    const hasOnlyPageKeys = pageKeys.every((key) => allowedPageKeys.some((allowedKey) => allowedKey === key));
    const hasResolvedBorder = Object.hasOwn(value, "resolvedBorder");
    const hasResolvedBorderRadius = Object.hasOwn(value, "resolvedBorderRadius");
    const hasEnumerableResolvedBorder = pageKeys.includes("resolvedBorder");
    const hasEnumerableResolvedBorderRadius = pageKeys.includes("resolvedBorderRadius");
    const hasResolvedBoxShadow = Object.hasOwn(value, "resolvedBoxShadow");
    const hasEnumerableResolvedBoxShadow = pageKeys.includes("resolvedBoxShadow");
    return (value.kind === "page" &&
        hasRequiredPageKeys &&
        hasOnlyPageKeys &&
        typeof value.border === "string" &&
        typeof value.borderRadius === "string" &&
        typeof value.boxShadow === "string" &&
        (hasResolvedBorder
            ? hasEnumerableResolvedBorder &&
                (value.resolvedBorder === undefined ||
                    isWidgetFrameResolvedBorder(value.resolvedBorder))
            : value.resolvedBorder === undefined) &&
        (hasResolvedBorderRadius
            ? hasEnumerableResolvedBorderRadius &&
                (value.resolvedBorderRadius === undefined ||
                    isWidgetFrameResolvedBorderRadius(value.resolvedBorderRadius))
            : value.resolvedBorderRadius === undefined) &&
        (hasResolvedBoxShadow
            ? hasEnumerableResolvedBoxShadow &&
                (value.resolvedBoxShadow === undefined ||
                    isWidgetFrameResolvedBoxShadow(value.resolvedBoxShadow))
            : value.resolvedBoxShadow === undefined));
}
const areWidgetFrameResolvedSrgbColorsEqual = (a, b) => a.colorSpace === b.colorSpace &&
    a.red === b.red &&
    a.green === b.green &&
    a.blue === b.blue &&
    a.alpha === b.alpha;
const areWidgetFrameResolvedBordersEqual = (a, b) => {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    return (areWidgetFrameResolvedSrgbColorsEqual(a.color, b.color) &&
        a.style === b.style &&
        a.widthPx === b.widthPx);
};
const areWidgetFrameResolvedBorderRadiusCornersEqual = (a, b) => a.horizontalPx === b.horizontalPx && a.verticalPx === b.verticalPx;
const areWidgetFrameResolvedBorderRadiiEqual = (a, b) => {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    return (areWidgetFrameResolvedBorderRadiusCornersEqual(a.topLeft, b.topLeft) &&
        areWidgetFrameResolvedBorderRadiusCornersEqual(a.topRight, b.topRight) &&
        areWidgetFrameResolvedBorderRadiusCornersEqual(a.bottomRight, b.bottomRight) &&
        areWidgetFrameResolvedBorderRadiusCornersEqual(a.bottomLeft, b.bottomLeft));
};
const areWidgetFrameResolvedBoxShadowLayersEqual = (a, b) => areWidgetFrameResolvedSrgbColorsEqual(a.color, b.color) &&
    a.offsetXPx === b.offsetXPx &&
    a.offsetYPx === b.offsetYPx &&
    a.blurRadiusPx === b.blurRadiusPx &&
    a.spreadRadiusPx === b.spreadRadiusPx &&
    a.inset === b.inset;
const areWidgetFrameResolvedBoxShadowsEqual = (a, b) => {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    return (a.length === b.length &&
        a.every((layer, index) => {
            const otherLayer = b[index];
            return (otherLayer !== undefined &&
                areWidgetFrameResolvedBoxShadowLayersEqual(layer, otherLayer));
        }));
};
export function areWidgetFrameAppearancesEqual(a, b) {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    if (a.kind !== b.kind) {
        return false;
    }
    if (a.kind === "none") {
        return true;
    }
    if (b.kind !== "page") {
        return false;
    }
    return (a.border === b.border &&
        a.borderRadius === b.borderRadius &&
        a.boxShadow === b.boxShadow &&
        areWidgetFrameResolvedBordersEqual(a.resolvedBorder, b.resolvedBorder) &&
        areWidgetFrameResolvedBorderRadiiEqual(a.resolvedBorderRadius, b.resolvedBorderRadius) &&
        areWidgetFrameResolvedBoxShadowsEqual(a.resolvedBoxShadow, b.resolvedBoxShadow));
}
export function cloneWidgetFrameAppearance(appearance) {
    if (appearance === undefined || appearance.kind === "none") {
        return appearance === undefined ? undefined : { kind: "none" };
    }
    return {
        ...appearance,
        ...(appearance.resolvedBorder === undefined
            ? {}
            : {
                resolvedBorder: {
                    ...appearance.resolvedBorder,
                    color: { ...appearance.resolvedBorder.color },
                },
            }),
        ...(appearance.resolvedBorderRadius === undefined
            ? {}
            : {
                resolvedBorderRadius: {
                    topLeft: { ...appearance.resolvedBorderRadius.topLeft },
                    topRight: { ...appearance.resolvedBorderRadius.topRight },
                    bottomRight: { ...appearance.resolvedBorderRadius.bottomRight },
                    bottomLeft: { ...appearance.resolvedBorderRadius.bottomLeft },
                },
            }),
        ...(appearance.resolvedBoxShadow === undefined
            ? {}
            : {
                resolvedBoxShadow: appearance.resolvedBoxShadow.map((layer) => ({
                    ...layer,
                    color: { ...layer.color },
                })),
            }),
    };
}
